import deepEquals from 'Lib/Object/deepEquals';
import { MdRemoveCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../Data/ReduxViewModels/Admin';
import {
  Button,
  ButtonGroup,
  ConfirmDialog,
  Dropdown,
  Field,
  Message,
  NumericInput,
  PlaceholderButton,
  TextInput,
} from '../../Core';
import { ColorPickerInput } from '../../Core/ColorPickerInput/ColorPickerInput';
import { FieldSet, ListFieldSection, PageSection, TabsSection } from '../../Layout';

const ReportsAdmin = () => {
  const installFonts = useSelector((state) => state.admin.syncModel.ReportStyle?.Fonts?.InstalledFonts, deepEquals);
  const fontOptions = (installFonts || []).map((font) => ({
    value: font.Value,
    label: font.Label,
  }));

  return (
    <>
      <PageSection heading="Fonts">
        <TabsSection
          data={[
            {
              menuItem: 'Font Family',
              render: () => (
                <ListFieldSection single>
                  <Field
                    input={<Dropdown />}
                    label="Regular"
                    name="syncModel.ReportStyle.Fonts.FontFamilyRegular"
                    options={fontOptions}
                    simpleValue
                  />
                  <Field
                    label="Bold"
                    input={<Dropdown />}
                    name="syncModel.ReportStyle.Fonts.FontFamilyBold"
                    options={fontOptions}
                    simpleValue
                  />
                  <Field
                    label="Italic"
                    input={<Dropdown />}
                    name="syncModel.ReportStyle.Fonts.FontFamilyItalic"
                    options={fontOptions}
                    simpleValue
                  />
                </ListFieldSection>
              ),
            },
            {
              menuItem: 'Title Font',
              render: () => (
                <>
                  <ListFieldSection single>
                    <Field
                      label="Font"
                      input={<Dropdown />}
                      name="syncModel.ReportStyle.Fonts.TitleFont.Font"
                      options={fontOptions}
                      simpleValue
                    />
                    <Field label="Size" input={<NumericInput />} name="syncModel.ReportStyle.Fonts.TitleFont.Size" />
                  </ListFieldSection>
                </>
              ),
            },
            {
              menuItem: 'Prompt Font',
              render: () => (
                <>
                  <ListFieldSection single>
                    <Field
                      label="Font"
                      input={<Dropdown />}
                      name="syncModel.ReportStyle.Fonts.PromptFont.Font"
                      options={fontOptions}
                      simpleValue
                    />
                    <Field label="Size" input={<NumericInput />} name="syncModel.ReportStyle.Fonts.PromptFont.Size" />
                  </ListFieldSection>
                </>
              ),
            },
            {
              menuItem: 'Body Font',
              render: () => (
                <>
                  <ListFieldSection single>
                    <Field
                      label="Font"
                      input={<Dropdown />}
                      name="syncModel.ReportStyle.Fonts.BodyFont.Font"
                      options={fontOptions}
                      simpleValue
                    />
                    <Field label="Size" input={<NumericInput />} name="syncModel.ReportStyle.Fonts.BodyFont.Size" />
                  </ListFieldSection>
                </>
              ),
            },
          ]}
        />
      </PageSection>
      <PageSection heading="Colors">
        <ListFieldSection>
          <Field
            label="Header Background"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.HeaderBackColor"
          />
          <Field
            label="Header Content"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.HeaderContentColor"
          />
          <Field
            label="Title Content"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.TitleContentColor"
          />
          <Field
            label="Prompt Content"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.PromptContentColor"
          />
          <Field
            label="Body Content"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.BodyContentColor"
          />
          <Field
            label="Highlight Content"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.HighlightContentColor"
          />
          <Field
            label="Table Border"
            input={<ColorPickerInput />}
            name="syncModel.ReportStyle.Colors.BorderTableColor"
          />
          <Field label="Grid Border" input={<ColorPickerInput />} name="syncModel.ReportStyle.Colors.BorderGridColor" />
        </ListFieldSection>
        <ListFieldSection>
          <Message info>Clearing a report color will set it back to its default color.</Message>
        </ListFieldSection>
      </PageSection>
      <PageSection heading="Builders">
        <BuildersSection />
      </PageSection>
    </>
  );
};

const BuilderSection = ({ builder, index }) => {
  if (!builder) return null;

  const headerOptions = (builder.InstalledHeaders || []).map((font) => ({
    value: font.Value,
    label: font.Label,
  }));

  const reviewOptions = (builder.InstalledReviews || []).map((font) => ({
    value: font.Value,
    label: font.Label,
  }));

  const sharedPageFooterOptions = (builder.InstalledSharedPageFooters || []).map((font) => ({
    value: font.Value,
    label: font.Label,
  }));

  const footerOptions = (builder.InstalledFooters || []).map((font) => ({
    value: font.Value,
    label: font.Label,
  }));

  return (
    <FieldSet key={index}>
      <Field label="Name" input={<TextInput />} name={`syncModel.ReportBuilders[${index}].Name`} />
      <Field
        label="ReportQestId"
        input={<NumericInput />}
        format={{ nearest: 1 }}
        name={`syncModel.ReportBuilders[${index}].ReportQestId`}
        additionalInformationDialogContent={'Enter 0 to apply to all reports.'}
      />
      <Field
        label="Header"
        input={<Dropdown />}
        name={`syncModel.ReportBuilders[${index}].Header`}
        options={headerOptions}
        simpleValue
      />
      <Field
        label="Review"
        input={<Dropdown />}
        name={`syncModel.ReportBuilders[${index}].Review`}
        options={reviewOptions}
        simpleValue
      />
      <Field
        label="SharedPageFooter"
        input={<Dropdown />}
        name={`syncModel.ReportBuilders[${index}].SharedPageFooter`}
        options={sharedPageFooterOptions}
        simpleValue
      />
      <Field
        label="Footer"
        input={<Dropdown />}
        name={`syncModel.ReportBuilders[${index}].Footer`}
        options={footerOptions}
        simpleValue
      />
    </FieldSet>
  );
};

const BuildersSection = () => {
  const builders = useSelector((state) => state.admin.syncModel.ReportBuilders, deepEquals);
  const dispatch = useDispatch();

  const removeBuilder = (index) => {
    dispatch(
      actions.setValue({
        name: 'syncModel.ReportBuilders',
        value: builders.filter((_, i) => i !== index),
        sync: true,
      })
    );
  };

  const addBuilder = () => {
    const exists = (name) => {
      return builders.some((c) => c.Name === name);
    };
    let index = builders.length;
    let name = `NewBuilder_${index}`;
    while (exists(name)) {
      index = index + 1;
      name = `NewBuilder_${index}`;
    }

    dispatch(actions.setValue({ name: 'syncModel.ReportBuilders', value: [...builders, { Name: name }], sync: true }));
  };

  return (
    <ListFieldSection single>
      {builders
        ? builders.map((_, i) => [
            <FieldSet key={i}>
              <BuilderSection builder={_} index={i} />
              <ButtonGroup align="center">
                <ConfirmDialog
                  confirm
                  confirmText={`Are you sure you want to delete the report builder '${_.Name}'?`}
                  confirmButtonText="Delete Report Builder"
                  confirmTitle="Delete Report Builder"
                >
                  <Button name={_.Name} subtle error onClick={() => removeBuilder(i)}>
                    <MdRemoveCircle size="2em" />
                  </Button>
                </ConfirmDialog>
              </ButtonGroup>
            </FieldSet>,
          ])
        : null}
      <div style={{ height: 190, display: 'flex' }}>
        <PlaceholderButton style={{ height: 200 }} onClick={() => addBuilder()} />
      </div>
    </ListFieldSection>
  );
};

export default ReportsAdmin;
export { ReportsAdmin };
